import React, { useRef, useEffect, useCallback, useMemo, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";

if (Quill && typeof Quill.register === "function") {
  Quill.register("modules/imageResize", ImageResize);
}

const Delta = Quill.import("delta");
const Embed = Quill.import("blots/embed");

class MentionBlot extends Embed {
  static blotName = "mention";
  static tagName = "span";
  static className = "ql-mention-chip";

  static create(value) {
    const node = super.create();
    const data = value || {};
    const id = String(data.id || "");
    const name = String(data.name || "");
    const email = String(data.email || "");
    node.setAttribute("data-mention-id", id);
    node.setAttribute("data-mention-name", name);
    node.setAttribute("data-mention-email", email);
    node.setAttribute("contenteditable", "false");
    node.style.background = "#dbeafe";
    node.style.borderRadius = "8px";
    node.style.padding = "1px 6px";
    node.style.color = "#1e3a8a";
    node.style.fontWeight = "600";
    node.style.whiteSpace = "nowrap";
    node.textContent = `[${name}] (${email})`;
    return node;
  }

  static value(node) {
    return {
      id: node.getAttribute("data-mention-id") || "",
      name: node.getAttribute("data-mention-name") || "",
      email: node.getAttribute("data-mention-email") || "",
    };
  }
}

if (Quill && typeof Quill.register === "function") {
  Quill.register(MentionBlot);
}

// -------------------------------
// Quill Modules & Formats
// -------------------------------

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link", "video"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "indent",
  "color",
  "background",
  "align",
  "link",
  "image",
  "video",
  "mention",
];

// -------------------------------
// Rich Text Editor Component
// -------------------------------

const RichTextEditor = ({
  value,
  onChange,
  onOutputChange,
  placeholder = "Enter text...",
  readOnly = false,
  label = null,
  height = "250px",
  className = "",
  mentionUsers = [],
  onImagePaste, // Custom callback for handling pasted images (optional)
  debugId = "RichTextEditor",
  modules: modulesProp = null,
}) => {
  const quillRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(null);
  const [mentionPosition, setMentionPosition] = useState({ left: 0, top: 0 });
  const mentionOpenRef = useRef(mentionOpen);
  const mentionStartRef = useRef(mentionStart);

  useEffect(() => {
    mentionOpenRef.current = mentionOpen;
  }, [mentionOpen]);
  useEffect(() => {
    mentionStartRef.current = mentionStart;
  }, [mentionStart]);

  const isMentionsEnabled = useMemo(
    () => !readOnly && Array.isArray(mentionUsers) && mentionUsers.length > 0,
    [mentionUsers, readOnly],
  );

  const filteredMentionUsers = useMemo(() => {
    if (!isMentionsEnabled) return [];
    const query = mentionQuery.trim().toLowerCase();
    return mentionUsers
      .filter((user) => {
        const name = String(user?.name || "").toLowerCase();
        const email = String(user?.email || "").toLowerCase();
        if (!query) return true;
        return name.includes(query) || email.includes(query);
      })
      .slice(0, 20);
  }, [isMentionsEnabled, mentionQuery, mentionUsers]);

  useEffect(() => {
    if (mentionIndex >= filteredMentionUsers.length) {
      setMentionIndex(0);
    }
  }, [filteredMentionUsers.length, mentionIndex]);
  // Keep the latest onImagePaste to avoid stale closures when the prop changes
  const onImagePasteRef = useRef(onImagePaste);
  useEffect(() => {
    onImagePasteRef.current = onImagePaste;
  }, [onImagePaste]);

  const emitOutput = useCallback((quill) => {
    if (!quill) return;
    const html = quill.root?.innerHTML || "";
    onChange?.(html);

    if (onOutputChange) {
      const delta = quill.getContents();
      const mentions = [];
      (delta.ops || []).forEach((op) => {
        if (op?.insert?.mention) {
          const m = op.insert.mention;
          mentions.push({
            id: String(m.id || ""),
            name: String(m.name || ""),
            email: String(m.email || ""),
          });
        }
      });
      onOutputChange({
        html,
        json: JSON.stringify(delta),
        mentions,
      });
    }
  }, [onChange, onOutputChange]);

  const closeMentions = useCallback(() => {
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(null);
    setMentionIndex(0);
  }, []);

  const updateMentionState = useCallback((quill) => {
    if (!quill || !isMentionsEnabled) {
      closeMentions();
      return;
    }

    const range = quill.getSelection();
    if (!range || range.length > 0) {
      closeMentions();
      return;
    }

    const prefix = quill.getText(0, range.index);
    const match = prefix.match(/(^|\s)@([a-zA-Z0-9._-]*)$/);
    if (!match) {
      closeMentions();
      return;
    }

    const query = match[2] || "";
    const atIndex = range.index - query.length - 1;
    if (atIndex < 0) {
      closeMentions();
      return;
    }

    const bounds = quill.getBounds(range.index);
    setMentionStart(atIndex);
    setMentionQuery(query);
    setMentionPosition({
      left: Math.max(8, bounds.left),
      top: bounds.top + bounds.height + 6,
    });
    setMentionOpen(true);
  }, [closeMentions, isMentionsEnabled]);

  const insertMention = useCallback((user) => {
    const quill = quillRef.current?.getEditor();
    const startIndex = mentionStartRef.current;
    if (!quill || startIndex === null || startIndex === undefined || !user) {
      return;
    }

    const range = quill.getSelection() || { index: quill.getLength() };
    const replaceLength = Math.max(0, range.index - startIndex);
    quill.deleteText(startIndex, replaceLength, "user");
    quill.insertEmbed(
      startIndex,
      "mention",
      {
        id: String(user.id || ""),
        name: String(user.name || ""),
        email: String(user.email || ""),
      },
      "user",
    );
    quill.insertText(startIndex + 1, " ", "user");
    quill.setSelection(startIndex + 2, 0, "silent");
    emitOutput(quill);
    closeMentions();
  }, [closeMentions, emitOutput]);

  console.info("[RichTextEditor] render", {
    debugId,
    label,
    hasOnImagePaste: !!onImagePaste,
  });

  const handlePaste = useCallback(
    (e) => {
      console.info("[RichTextEditor] paste event received", { debugId });
      const items = e.clipboardData?.items;
      const filesFallback = e.clipboardData?.files;
      if (!items && !filesFallback) {
        console.info("[RichTextEditor] no clipboard items", { debugId });
        return;
      }

      let imageItem = null;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            imageItem = items[i];
            break;
          }
        }
      }
      if (!imageItem && filesFallback && filesFallback.length > 0) {
        const file = filesFallback[0];
        if (file.type?.indexOf("image") !== -1) {
          imageItem = { getAsFile: () => file, type: file.type };
        }
      }

      if (!imageItem) {
        console.info("[RichTextEditor] no image item, allowing default paste", {
          debugId,
        });
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
      console.info("[RichTextEditor] default paste prevented for image", {
        debugId,
      });
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      console.info(
        "[RichTextEditor] image item detected; delegating to onImagePaste",
        { debugId },
      );

      const range = quill.getSelection() || {
        index: quill.getLength(),
        length: 0,
      };
      const baseIndex = range.index;
      const file = imageItem.getAsFile();

      const globalFallback =
        typeof window !== "undefined"
          ? window.__globalImagePasteHandler ||
            window.__taskFormImagePaste ||
            null
          : null;
      const pasteHandler = onImagePasteRef.current || globalFallback;
      console.info("[RichTextEditor] pasteHandler present?", !!pasteHandler, {
        debugId,
        usedGlobalFallback: !!(!onImagePasteRef.current && globalFallback),
      });

      const syncHtml = () => {
        emitOutput(quill);
        return quill.root?.innerHTML || "";
      };

      if (pasteHandler) {
        const helpers = {
          insertPlaceholder: (text) => {
            const targetIndex =
              quill.getSelection()?.index ?? baseIndex ?? quill.getLength();
            quill.focus();
            quill.insertText(targetIndex, text, { color: "#1677ff" }, "user");
            console.info("[RichTextEditor] inserted placeholder", {
              text,
              targetIndex,
              debugId,
            });
            return {
              index: targetIndex,
              length: text.length,
              html: syncHtml(),
            };
          },
          replacePlaceholderWithText: (placeholderRange, text) => {
            quill.deleteText(
              placeholderRange.index,
              placeholderRange.length,
              "user",
            );
            quill.insertText(placeholderRange.index, text, "user");
            syncHtml();
          },
          replacePlaceholderWithImage: (placeholderRange, imageUrl) => {
            quill.deleteText(
              placeholderRange.index,
              placeholderRange.length,
              "user",
            );
            quill.insertEmbed(
              placeholderRange.index,
              "image",
              imageUrl,
              "user",
            );
            console.info(
              "[RichTextEditor] placeholder replaced with image embed",
              {
                imageUrl,
                index: placeholderRange.index,
                debugId,
              },
            );
            syncHtml();
          },
          getHtml: syncHtml,
        };

        pasteHandler(file, helpers);
      } else {
        const fallbackText = "![Uploading image…](upload://not-handled)";
        const targetIndex =
          quill.getSelection()?.index ?? baseIndex ?? quill.getLength();
        quill.focus();
        quill.insertText(targetIndex, fallbackText, "user");
        console.info(
          "[RichTextEditor] onImagePaste not provided; inserted fallback placeholder",
          { debugId },
        );
        syncHtml();
      }
  }, [debugId, emitOutput]);

  useEffect(() => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();

    // Block any image content that Quill clipboard would normally insert
    const imageMatcher = (node, delta) => {
      if (node.tagName && node.tagName.toLowerCase() === "img") {
        const src = node.getAttribute("src") || "";
        const shouldBlock =
          src.startsWith("data:") ||
          src.startsWith("blob:") ||
          src.startsWith("upload://");
        if (shouldBlock) {
          return new Delta();
        }
      }
      return delta || new Delta();
    };
    const mentionMatcher = (node, delta) => {
      if (!node?.getAttribute) return delta || new Delta();
      const id = node.getAttribute("data-mention-id");
      const name = node.getAttribute("data-mention-name");
      const email = node.getAttribute("data-mention-email");
      if (!id || !name) return delta || new Delta();
      return new Delta().insert({
        mention: {
          id: String(id),
          name: String(name),
          email: String(email || ""),
        },
      });
    };

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, imageMatcher);
    quill.clipboard.addMatcher("span", mentionMatcher);
    const editor = quill.root;
    editor.addEventListener("paste", handlePaste, true);

    const handleTextChange = (_delta, _oldDelta, source) => {
      if (source !== "user") return;
      emitOutput(quill);
      updateMentionState(quill);
    };
    const handleSelectionChange = () => {
      updateMentionState(quill);
    };
    const handleKeyDown = (event) => {
      if (!mentionOpenRef.current) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionIndex((prev) =>
          filteredMentionUsers.length === 0 ? 0 : (prev + 1) % filteredMentionUsers.length,
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionIndex((prev) =>
          filteredMentionUsers.length === 0
            ? 0
            : (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length,
        );
      } else if (event.key === "Enter" || event.key === "Tab") {
        if (filteredMentionUsers.length > 0) {
          event.preventDefault();
          insertMention(filteredMentionUsers[mentionIndex] || filteredMentionUsers[0]);
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        closeMentions();
      }
    };

    quill.on("text-change", handleTextChange);
    quill.on("selection-change", handleSelectionChange);
    editor.addEventListener("keydown", handleKeyDown, true);

    return () => {
      editor.removeEventListener("paste", handlePaste, true);
      editor.removeEventListener("keydown", handleKeyDown, true);
      quill.off("text-change", handleTextChange);
      quill.off("selection-change", handleSelectionChange);
      quill.clipboard.matchers = quill.clipboard.matchers.filter(
        ([, matcher]) => matcher !== imageMatcher && matcher !== mentionMatcher,
      );
    };
  }, [
    closeMentions,
    emitOutput,
    filteredMentionUsers,
    handlePaste,
    insertMention,
    mentionIndex,
    readOnly,
    updateMentionState,
  ]);

  const handleEditorChange = useCallback((html, delta, source, editor) => {
    if (source === "api") {
      onChange?.(html);
      if (onOutputChange) {
        const currentDelta = editor.getContents();
        const mentions = [];
        (currentDelta.ops || []).forEach((op) => {
          if (op?.insert?.mention) {
            mentions.push({
              id: String(op.insert.mention.id || ""),
              name: String(op.insert.mention.name || ""),
              email: String(op.insert.mention.email || ""),
            });
          }
        });
        onOutputChange({ html, json: JSON.stringify(currentDelta), mentions });
      }
    }
  }, [onChange, onOutputChange]);

  const modules = modulesProp || {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["link", "video"],
      ["clean"],
    ],
    clipboard: { matchVisual: false },
    imageResize: {},
  };

  return (
    <div className={`mb-3 ${className}`}>
      {label && <label className="form-label fw-semibold">{label}</label>}

      {/* Editor Border Wrapper */}
      <div style={{ position: "relative" }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={handleEditorChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
          className="auto-grow-editor"
        />

        {mentionOpen && (
          <div
            style={{
              position: "absolute",
              left: mentionPosition.left,
              top: mentionPosition.top,
              zIndex: 9999,
              width: 320,
              maxHeight: 240,
              overflowY: "auto",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              background: "#ffffff",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            {filteredMentionUsers.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: 13, color: "#6b7280" }}>
                No teammates found
              </div>
            ) : (
              filteredMentionUsers.map((user, idx) => {
                const active = idx === mentionIndex;
                return (
                  <button
                    key={user.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(user);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      background: active ? "#eff6ff" : "#ffffff",
                      textAlign: "left",
                      padding: "10px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {user.name}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email}</div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
